import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { getTier, tierMass, tierRadius, type TierDef } from '../art/tiers';
import { getGeometryForTier } from '../art/geometry';
import { getMaterialForTier } from '../art/materials';
import { createFacePlane } from '../art/faces';
import { PHYSICS } from '../config';
import { clamp, easeOutElastic, lerp } from '../utils/math';

let nextId = 1;

export class Confection {
  readonly id: number;
  tier: number;
  readonly mesh: THREE.Group;
  body: RAPIER.RigidBody | null = null;
  collider: RAPIER.Collider | null = null;
  radius: number;
  def: TierDef;

  /** Visual-only state */
  private bodyMesh: THREE.Mesh;
  private face: THREE.Mesh;
  private squash = 1;
  private stretch = 1;
  private impactTimer = 0;
  private spawnTimer = 0;
  private mergePop = 0;
  private idlePhase: number;
  private disposed = false;
  /** Prevent double-merge */
  merging = false;
  markedForRemoval = false;
  /** Time continuously above danger line */
  dangerTime = 0;

  constructor(tier: number) {
    this.id = nextId++;
    this.tier = tier;
    this.def = getTier(tier);
    this.radius = tierRadius(tier);
    this.idlePhase = Math.random() * Math.PI * 2;

    this.mesh = new THREE.Group();
    this.mesh.name = `confection-${this.id}`;

    const geo = getGeometryForTier(this.def);
    const mat = getMaterialForTier(this.def).clone();
    // Slight per-instance color variance
    const c = mat.color.clone();
    c.offsetHSL((Math.random() - 0.5) * 0.03, (Math.random() - 0.5) * 0.05, (Math.random() - 0.5) * 0.04);
    mat.color = c;

    this.bodyMesh = new THREE.Mesh(geo, mat);
    this.bodyMesh.castShadow = true;
    this.bodyMesh.receiveShadow = true;
    this.bodyMesh.scale.setScalar(this.radius);
    this.mesh.add(this.bodyMesh);

    this.face = createFacePlane(this.def, 1);
    this.face.scale.setScalar(this.radius);
    // Face on body mesh so it scales with squash
    this.bodyMesh.add(this.face);

    // Soft highlight “catch light” for premium candy feel
    const hl = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 10, 8),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.22 + this.def.softness * 0.12,
        depthWrite: false,
      }),
    );
    hl.position.set(-0.28, 0.32, 0.55);
    hl.scale.set(1, 0.7, 0.5);
    hl.name = 'highlight';
    this.bodyMesh.add(hl);

    // Accent ring for mid/high tiers
    if (this.tier >= 3) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.92, 0.04, 6, 24),
        new THREE.MeshStandardMaterial({
          color: this.def.accent,
          emissive: new THREE.Color(this.def.emissive),
          emissiveIntensity: 0.35,
          roughness: 0.4,
          metalness: 0.2,
          transparent: true,
          opacity: 0.85,
        }),
      );
      ring.rotation.x = Math.PI / 2.4;
      ring.position.y = -0.15;
      ring.name = 'accent-ring';
      this.bodyMesh.add(ring);
    }

    this.spawnTimer = 0.35;
    this.mergePop = 1;
  }

  createPhysics(world: RAPIER.World, x: number, y: number, dynamic = true): void {
    const mass = tierMass(this.tier);
    const desc = dynamic
      ? RAPIER.RigidBodyDesc.dynamic()
          .setTranslation(x, y, 0)
          .setLinearDamping(PHYSICS.linearDamping)
          .setAngularDamping(PHYSICS.angularDamping)
          .setCcdEnabled(true)
          .setCanSleep(true)
      : RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(x, y, 0);

    this.body = world.createRigidBody(desc);

    const restitution = clamp(
      PHYSICS.restitutionBase - this.tier * PHYSICS.restitutionDecay,
      0.05,
      0.35,
    );
    const friction = PHYSICS.frictionBase + this.tier * PHYSICS.frictionGrowth;

    const collDesc = RAPIER.ColliderDesc.ball(this.radius * 0.92)
      .setRestitution(restitution)
      .setFriction(friction)
      .setDensity(mass / ((4 / 3) * Math.PI * Math.pow(this.radius * 0.92, 3)))
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS)
      .setCollisionGroups(0x00010001);

    this.collider = world.createCollider(collDesc, this.body);

    // Lock Z translation and X/Y rotation for 2.5D play
    if (dynamic) {
      this.body.setEnabledTranslations(true, true, false, true);
      this.body.setEnabledRotations(false, false, true, true);
    }
  }

  setKinematic(_world: RAPIER.World, kinematic: boolean): void {
    if (!this.body) return;
    // Recreate is expensive; use setBodyType
    this.body.setBodyType(
      kinematic ? RAPIER.RigidBodyType.KinematicPositionBased : RAPIER.RigidBodyType.Dynamic,
      true,
    );
    if (!kinematic) {
      this.body.setLinearDamping(PHYSICS.linearDamping);
      this.body.setAngularDamping(PHYSICS.angularDamping);
      this.body.setEnabledTranslations(true, true, false, true);
      this.body.setEnabledRotations(false, false, true, true);
      this.body.wakeUp();
    }
  }

  setTranslation(x: number, y: number): void {
    if (!this.body) {
      this.mesh.position.set(x, y, 0);
      return;
    }
    this.body.setTranslation({ x, y, z: 0 }, true);
    this.mesh.position.set(x, y, 0);
  }

  getPosition(): THREE.Vector3 {
    if (this.body) {
      const t = this.body.translation();
      return new THREE.Vector3(t.x, t.y, t.z);
    }
    return this.mesh.position.clone();
  }

  applyImpulse(x: number, y: number): void {
    if (!this.body || this.body.bodyType() !== RAPIER.RigidBodyType.Dynamic) return;
    this.body.applyImpulse({ x, y, z: 0 }, true);
  }

  onImpact(strength: number): void {
    this.impactTimer = Math.min(0.25, 0.08 + strength * 0.05);
    this.squash = clamp(1 - strength * 0.12, 0.7, 1);
    this.stretch = clamp(1 + strength * 0.1, 1, 1.25);
  }

  playMergeBirth(): void {
    this.mergePop = 1;
    this.spawnTimer = 0.4;
  }

  syncFromPhysics(dt: number, time: number): void {
    if (this.disposed || !this.body) return;
    const t = this.body.translation();
    const r = this.body.rotation();
    this.mesh.position.set(t.x, t.y, 0);
    this.mesh.quaternion.set(r.x, r.y, r.z, r.w);

    // Clamp velocities
    const lv = this.body.linvel();
    const speed = Math.hypot(lv.x, lv.y);
    if (speed > PHYSICS.maxLinearVelocity) {
      const s = PHYSICS.maxLinearVelocity / speed;
      this.body.setLinvel({ x: lv.x * s, y: lv.y * s, z: 0 }, true);
    }
    const av = this.body.angvel();
    if (Math.abs(av.z) > PHYSICS.maxAngularVelocity) {
      this.body.setAngvel(
        { x: 0, y: 0, z: Math.sign(av.z) * PHYSICS.maxAngularVelocity },
        true,
      );
    }

    this.updateVisual(dt, time);
  }

  updateVisual(dt: number, time: number): void {
    // Spawn / merge pop
    if (this.spawnTimer > 0) {
      this.spawnTimer -= dt;
      const t = 1 - clamp(this.spawnTimer / 0.4, 0, 1);
      const pop = easeOutElastic(t);
      this.mergePop = pop;
    } else {
      this.mergePop = lerp(this.mergePop, 1, 1 - Math.pow(0.001, dt));
    }

    // Impact squash recover
    if (this.impactTimer > 0) {
      this.impactTimer -= dt;
      this.squash = lerp(this.squash, 1, 1 - Math.pow(0.0001, dt));
      this.stretch = lerp(this.stretch, 1, 1 - Math.pow(0.0001, dt));
    } else {
      this.squash = lerp(this.squash, 1, 8 * dt);
      this.stretch = lerp(this.stretch, 1, 8 * dt);
    }

    // Idle bob / breathe
    const bob = Math.sin(time * 2.2 + this.idlePhase) * this.def.idleBob;
    const breathe = 1 + Math.sin(time * 1.6 + this.idlePhase) * 0.02 * this.def.softness;

    const s = this.radius * this.mergePop * breathe;
    this.bodyMesh.scale.set(
      s * this.stretch,
      s * this.squash + bob * this.radius * 0.15,
      s * this.stretch,
    );

    // Face always looks roughly at camera (billboard-ish on local Z)
    // Keep face upright relative to world by counter-rotating parent spin slightly
  }

  isAboveDanger(dangerY: number): boolean {
    const pos = this.getPosition();
    // Center of mass above line counts (matches classic merge-puzzle feel)
    return pos.y > dangerY - this.radius * 0.15;
  }

  dispose(world: RAPIER.World | null): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.body && world) {
      world.removeRigidBody(this.body);
    }
    this.body = null;
    this.collider = null;
    // Dispose instance materials (shared geometries stay cached)
    this.bodyMesh.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else if (mat) mat.dispose();
      // Only dispose non-shared geometries (face plane, highlight, accent)
      if (mesh !== this.bodyMesh && mesh.geometry) {
        mesh.geometry.dispose();
      }
    });
    this.mesh.removeFromParent();
  }
}
